import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import TerminalIcon from "@mui/icons-material/Terminal";
import PageShell from "./PageShell";
import PageContainer from "./components/PageContainer";
import PageHeader from "./components/PageHeader";
import DataCard, { tableHeaderCellSx } from "./components/DataCard";
import apiClient from "./utils/apiClient";

const LAST_QUERY_STORAGE_KEY = "continuous_mfa.sql_debug.last_query";
const DEFAULT_QUERY = "SELECT tablename FROM pg_tables WHERE schemaname = current_schema() ORDER BY tablename;";

const formatCell = (value) => {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch (_error) {
    return String(value);
  }
};

export default function SqlDebug() {
  const [sql, setSql] = useState(() => {
    try {
      return localStorage.getItem(LAST_QUERY_STORAGE_KEY) || DEFAULT_QUERY;
    } catch (_error) {
      return DEFAULT_QUERY;
    }
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    try {
      localStorage.setItem(LAST_QUERY_STORAGE_KEY, sql);
    } catch (_error) {
      // Best-effort convenience only — a private window or full storage
      // quota must not block running the query.
    }
  }, [sql]);

  const runQuery = async () => {
    if (!sql.trim() || loading) return;
    setLoading(true);
    setError(null);
    try {
      const data = await apiClient.post("/admin/sql-query", { sql });
      setResult(data);
    } catch (err) {
      console.error("SQL debug query failed:", err);
      setError(err?.message || String(err) || "Query failed. Check the SQL and try again.");
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
      e.preventDefault();
      runQuery();
    }
  };

  const columns = useMemo(() => {
    const records = result?.records || [];
    const set = new Set();
    for (const row of records) for (const key of Object.keys(row)) set.add(key);
    const all = Array.from(set);
    const hasId = all.includes("id");
    const rest = all.filter((k) => k !== "id").sort();
    return hasId ? ["id", ...rest] : rest;
  }, [result]);

  return (
    <PageShell>
      <PageContainer>
        <PageHeader
          icon={<TerminalIcon />}
          title="SQL Debug"
          subtitle="Read-only — writes are rejected by the database, every query is audit-logged"
        />

        {error && <Alert severity="error">{error}</Alert>}

        <DataCard>
          <Box
            component="textarea"
            value={sql}
            onChange={(e) => setSql(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={8}
            placeholder="SELECT ..."
            sx={{
              width: "100%",
              fontFamily: "monospace",
              fontSize: "0.85rem",
              p: 1.5,
              borderRadius: 1,
              border: "1px solid #E2E8F0",
              backgroundColor: "#0F172A",
              color: "#E2E8F0",
              resize: "vertical",
              boxSizing: "border-box",
            }}
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 1.5 }}>
            <Button
              variant="contained"
              onClick={runQuery}
              disabled={loading || !sql.trim()}
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <PlayArrowIcon />}
            >
              {loading ? "Running..." : "Run Query"}
            </Button>
            <Typography variant="caption" color="text.secondary">
              Ctrl+Enter to run
            </Typography>
          </Box>
        </DataCard>

        {result && (
          <DataCard
            noPadding
            title={`${result.row_count} row${result.row_count === 1 ? "" : "s"}${result.truncated ? " (truncated)" : ""}`}
            action={
              <Typography variant="caption" color="text.secondary">
                {result.duration_ms?.toFixed?.(1)} ms
              </Typography>
            }
          >
            {result.truncated && (
              <Alert severity="warning" sx={{ borderRadius: 0 }}>
                Showing the first {result.row_count} rows — refine your query (add a WHERE/LIMIT) to see more.
              </Alert>
            )}
            <TableContainer sx={{ overflowX: "auto", maxHeight: 600 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {columns.map((c) => (
                      <TableCell key={c} sx={tableHeaderCellSx}>{c}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.records.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={Math.max(columns.length, 1)} align="center" sx={{ py: 4, color: "text.secondary" }}>
                        Query returned no rows.
                      </TableCell>
                    </TableRow>
                  ) : (
                    result.records.map((row, i) => (
                      <TableRow key={row.id ?? i} hover>
                        {columns.map((c) => (
                          <TableCell key={c} sx={{ maxWidth: 420, wordBreak: "break-word", fontFamily: "monospace", fontSize: "0.78rem" }}>
                            {formatCell(row[c])}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </DataCard>
        )}
      </PageContainer>
    </PageShell>
  );
}
